/**
 * TXM-671 — vigência da rúbrica no eSocial (mensal, "YYYY-MM") para a coluna "Período de
 * vigência" e o filtro por período da aba Rúbricas.
 *
 * `vigencia = { inicio: "YYYY-MM", fim: "YYYY-MM" | null }`; fim nulo = em aberto. Uma
 * rúbrica pode ter várias (lista `vigencias` da API, só as que cobrem competências usadas
 * no período da apuração).
 */

export const MODOS_VIGENCIA = [
  { value: "sobrepoe", label: "Válida em algum mês do período" },
  { value: "cobre", label: "Válida em todo o período" },
  { value: "comecou", label: "Começou no período" },
  { value: "terminou", label: "Terminou no período" },
];

export const MODO_VIGENCIA_PADRAO = "sobrepoe";

const MES_RE = /^\d{4}-(0[1-9]|1[0-2])/;

/** "YYYY-MM[-DD]" → "YYYY-MM"; qualquer outra coisa → null. */
function mesDe(valor) {
  const s = valor == null ? "" : String(valor).trim();
  return MES_RE.test(s) ? s.slice(0, 7) : null;
}

/** "2021-04" → "04/2021"; inválido → "—". */
export function formatarMesAno(ym) {
  const m = mesDe(ym);
  return m ? `${m.slice(5)}/${m.slice(0, 4)}` : "—";
}

/** "04/2021 → 04/2024" ou "05/2024 → em aberto". */
export function textoVigencia(vigencia) {
  const fim = mesDe(vigencia?.fim);
  return `${formatarMesAno(vigencia?.inicio)} → ${fim ? formatarMesAno(fim) : "em aberto"}`;
}

/** Descarta itens sem início válido, normaliza o fim (vazio → null) e ordena por início. */
export function normalizarVigencias(lista) {
  if (!Array.isArray(lista)) return [];
  return lista
    .map((v) => {
      const inicio = mesDe(v?.inicio);
      return inicio ? { inicio, fim: mesDe(v?.fim) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.inicio.localeCompare(b.inicio));
}

/**
 * Uma vigência casa com o período [de, ate] (meses inclusivos)? `de`/`ate` vazios deixam o
 * lado aberto. Vigência em aberto (fim nulo) vale até hoje.
 *  - sobrepoe : vale em pelo menos um mês do período (padrão)
 *  - cobre    : vale em todos os meses do período
 *  - comecou  : o início está dentro do período
 *  - terminou : o fim está dentro do período (em aberto nunca termina)
 */
export function vigenciaCasaPeriodo(vigencia, de, ate, modo = MODO_VIGENCIA_PADRAO) {
  const inicio = mesDe(vigencia?.inicio);
  if (!inicio) return false;
  const fim = mesDe(vigencia?.fim);
  const a = mesDe(de) || "0000-01";
  const b = mesDe(ate) || "9999-12";
  switch (modo) {
    case "cobre":
      return inicio <= a && (fim === null || fim >= b);
    case "comecou":
      return inicio >= a && inicio <= b;
    case "terminou":
      return fim !== null && fim >= a && fim <= b;
    case "sobrepoe":
    default:
      return inicio <= b && (fim === null || fim >= a);
  }
}

/**
 * Lê um mês digitado no filtro: "MM/AAAA" (o formato da tela) ou "YYYY-MM".
 * Vazio → `{ vazio: true }`; válido → `{ mes: "YYYY-MM" }`; incompleto/inválido
 * ("06/20", "13/2022") → `{ invalido: true }`.
 */
export function lerMesDigitado(texto) {
  const s = texto == null ? "" : String(texto).trim();
  if (s === "") return { vazio: true };
  const br = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(s);
  if (br) return { mes: `${br[2]}-${br[1]}` };
  const iso = mesDe(s);
  return iso ? { mes: iso } : { invalido: true };
}

/**
 * Situação do período digitado no filtro de vigência:
 *  - "vazio"     : nada digitado (não filtra)
 *  - "ok"        : `{ de, ate }` em "YYYY-MM" (um dos lados pode ser null = aberto)
 *  - "formato"   : algum mês incompleto/inválido (não filtra; a tela avisa)
 *  - "invertido" : de > ate (não filtra; a tela avisa)
 */
export function avaliarPeriodoFiltro(de, ate) {
  const a = lerMesDigitado(de);
  const b = lerMesDigitado(ate);
  if (a.invalido || b.invalido) return { status: "formato" };
  if (a.vazio && b.vazio) return { status: "vazio" };
  const mesA = a.mes || null;
  const mesB = b.mes || null;
  if (mesA && mesB && mesA > mesB) return { status: "invertido" };
  return { status: "ok", de: mesA, ate: mesB };
}

/**
 * A rúbrica entra no filtro se QUALQUER uma das vigências casa. Sem período, com mês
 * incompleto ou com período invertido o filtro não se aplica (a tela avisa nos dois
 * últimos). Rúbrica sem vigência só some quando há um período válido.
 */
export function rubricaCasaVigencia(vigencias, de, ate, modo) {
  const periodo = avaliarPeriodoFiltro(de, ate);
  if (periodo.status !== "ok") return true;
  return normalizarVigencias(vigencias).some((v) =>
    vigenciaCasaPeriodo(v, periodo.de, periodo.ate, modo)
  );
}

/** `test` do catálogo de filtro: (linha, de, ate, filtroAtivo) — o modo vem do filtro ativo. */
export function testeVigencia(linha, de, ate, ativo) {
  return rubricaCasaVigencia(linha?.vigencias, de, ate, ativo?.modo);
}

function indiceMes(ym) {
  return Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5, 7));
}

/**
 * Posição da barra da vigência na linha do tempo (em %). A escala é o período da apuração;
 * a vigência é recortada nas bordas e a em aberto vai até o fim da escala.
 * @returns {{ esquerda: number, largura: number, aberta: boolean }} largura 0 = fora da escala
 */
export function posicaoNaEscala(vigencia, escala) {
  const inicio = mesDe(vigencia?.inicio);
  const fim = mesDe(vigencia?.fim);
  const eIni = mesDe(escala?.inicio);
  const eFim = mesDe(escala?.fim);
  const aberta = fim === null;
  if (!inicio || !eIni || !eFim) return { esquerda: 0, largura: 0, aberta };

  const total = indiceMes(eFim) - indiceMes(eIni) + 1;
  if (total <= 0) return { esquerda: 0, largura: 0, aberta };
  const de = Math.max(0, indiceMes(inicio) - indiceMes(eIni));
  const ate = Math.min(total - 1, (fim ? indiceMes(fim) : indiceMes(eFim)) - indiceMes(eIni));
  if (ate < de) return { esquerda: 0, largura: 0, aberta };
  return { esquerda: (de / total) * 100, largura: ((ate - de + 1) / total) * 100, aberta };
}

/**
 * Escala da linha do tempo: o período da apuração; sem ele, do menor início ao maior mês
 * conhecido entre as vigências. `null` quando não há nada para desenhar.
 */
export function escalaDasRubricas(rubricas, apuracao) {
  const ini = mesDe(apuracao?.periodoInicio || apuracao?.periodo_inicio);
  const fim = mesDe(apuracao?.periodoFim || apuracao?.periodo_fim);
  if (ini && fim && ini <= fim) return { inicio: ini, fim };

  const meses = [];
  let menor = null;
  (rubricas || []).forEach((r) => {
    normalizarVigencias(r?.vigencias).forEach((v) => {
      if (menor === null || v.inicio < menor) menor = v.inicio;
      meses.push(v.inicio);
      if (v.fim) meses.push(v.fim);
    });
  });
  if (menor === null) return null;
  return { inicio: menor, fim: meses.reduce((a, b) => (b > a ? b : a)) };
}

// ---- Régua de meses (opção L): início em cima, fim embaixo --------------------------------

/** Largura da régua (px); os meses dividem esse espaço igualmente. */
export const LARGURA_REGUA = 280;
const PX_POR_CARACTERE = 7.5;
const FOLGA_ROTULO = 4;
const MAX_LINHAS_ROTULO = 3;

/** Todos os meses "YYYY-MM" da escala, em ordem. */
export function mesesDaEscala(escala) {
  const ini = mesDe(escala?.inicio);
  const fim = mesDe(escala?.fim);
  if (!ini || !fim || ini > fim) return [];
  const meses = [];
  for (let i = indiceMes(ini); i <= indiceMes(fim); i += 1) {
    const ano = Math.floor((i - 1) / 12);
    meses.push(`${ano}-${String(i - ano * 12).padStart(2, "0")}`);
  }
  return meses;
}

/** Cobertura do mês: "aberta" (alguma vigência em aberto) > "fechada" > null (sem vigência). */
export function classeDoMes(mes, vigencias) {
  let classe = null;
  (vigencias || []).forEach((v) => {
    if (mes < v.inicio || (v.fim && mes > v.fim)) return;
    if (!v.fim) classe = "aberta";
    else if (!classe) classe = "fechada";
  });
  return classe;
}

const aaMes = (ym) => `${ym.slice(5, 7)}/${ym.slice(2, 4)}`;

/**
 * Um rótulo só por mês: marcos iguais viram "atual ×2" e marcos diferentes do mesmo mês são
 * unidos ("01/26 · atual"), em vez de empilhados. "atual" fica por último e a cor (`tipo`) é
 * a do primeiro.
 */
function agrupar(eventos) {
  const meses = new Map();
  eventos.forEach((e) => {
    const g = meses.get(e.indice) || new Map();
    const c = g.get(e.texto);
    g.set(e.texto, { e, n: (c?.n || 0) + 1 });
    meses.set(e.indice, g);
  });
  return [...meses.values()].map((g) => {
    const itens = [...g.values()].sort((a, b) => (a.e.tipo === "atual") - (b.e.tipo === "atual"));
    return {
      indice: itens[0].e.indice,
      tipo: itens[0].e.tipo,
      texto: itens.map(({ e, n }) => (n > 1 ? `${e.texto} ×${n}` : e.texto)).join(" · "),
    };
  });
}

/** Distribui os rótulos em até 3 linhas para não se sobreporem; devolve-os com `linha`. */
function escalonar(eventos, porMes) {
  const fimDaLinha = [];
  return eventos
    .sort((a, b) => a.indice - b.indice)
    .map((e) => {
      const largura = e.texto.length * PX_POR_CARACTERE + FOLGA_ROTULO;
      const centro = (e.indice + 0.5) * porMes;
      const esquerda = Math.min(Math.max(centro - largura / 2, 0), LARGURA_REGUA - largura);
      let linha = 0;
      while (linha < MAX_LINHAS_ROTULO - 1 && esquerda < (fimDaLinha[linha] ?? -Infinity)) linha += 1;
      fimDaLinha[linha] = esquerda + largura;
      return { ...e, linha, esquerda, largura, centro };
    });
}

/**
 * Marcos da régua: `inicios` (rótulo em cima) e `fins` (embaixo, "atual" se em aberto), com
 * `indice` do mês na escala. Início anterior à escala vira "◂" no 1º mês; vigência inteira
 * fora da escala não aparece.
 */
export function marcosDaRegua(vigencias, escala) {
  const meses = mesesDaEscala(escala);
  const vazio = { inicios: [], fins: [], linhasAcima: 0, linhasAbaixo: 0 };
  if (!meses.length) return vazio;
  const primeiro = meses[0];
  const ultimo = meses[meses.length - 1];
  const inicios = [];
  const fins = [];
  (vigencias || []).forEach((v) => {
    if (v.inicio > ultimo || (v.fim && v.fim < primeiro)) return;
    const antes = v.inicio < primeiro;
    inicios.push({
      indice: antes ? 0 : meses.indexOf(v.inicio),
      texto: `${antes ? "◂" : ""}${aaMes(v.inicio)}`,
      tipo: "inicio",
    });
    if (!v.fim) fins.push({ indice: meses.length - 1, texto: "atual", tipo: "atual" });
    else if (v.fim > ultimo) fins.push({ indice: meses.length - 1, texto: `${aaMes(v.fim)}▸`, tipo: "fim" });
    else fins.push({ indice: meses.indexOf(v.fim), texto: aaMes(v.fim), tipo: "fim" });
  });
  const porMes = LARGURA_REGUA / meses.length;
  const acima = escalonar(agrupar(inicios), porMes);
  const abaixo = escalonar(agrupar(fins), porMes);
  const linhas = (l) => (l.length ? Math.max(...l.map((e) => e.linha)) + 1 : 0);
  return { inicios: acima, fins: abaixo, linhasAcima: linhas(acima), linhasAbaixo: linhas(abaixo) };
}
